// 招待 URL と、グループ／チャンネルの名前の扱い。
//
// 形は `https://<ホスト>/#room=<グループ>/<チャンネル>` で**確定**（2026-10-19 のユーザー判断。
// **正本は DESIGN-0005 §4-A**。研究側はクエリ `?room=` のまま）。
//
// **グループだけを指す招待 URL もある**（2026-10-29 のユーザー判断）。鍵を分けて
// `#group=<コード>` にする。**チャンネルまで指すものと機械的に見分けられる**ことと、
// 常設の部屋の識別子（`main` のように `/` を含まないもの）と取り違える余地が無いこと。
// **両方あれば `room=` を優先する**（より具体的な行き先。{@link parseInviteTarget}）。
//
// クエリではなくフラグメントにする理由は、HTTP の要求にも Referer にも含まれないので
// nginx のログや他サイトへ漏れないこと（PLAN-0005 §6 の 10・ADR-0022 §2-2）。
// **`room=` の鍵を付ける理由は後から項目を足せること**（`#room=…&x=…`）と、
// ADR-0021 §1・ADR-0022 §1 の例示と揃うこと。研究側の実装（`URLSearchParams`）と
// 形が揃うので、どちらの版でも同じ読み方になる。
//
// **ここには純粋な関数だけを置く**（`location` も DOM も `localStorage` も触らない）。
// 読む側が文字列を渡す。理由は試験をコンテナ内で完結させられること（DESIGN-0006 §5-1）。
//
// 入室要求に入れる識別子は `グループ/チャンネル` の 1 本の文字列（ADR-0019 §5-2）。
// コードの `room` という呼び名はそのまま（同 §5-3）。
//
// # チャンネルは「識別子」と「表示名」の 2 つに分かれる（ADR-0022）
//
// **識別子はサーバが採番する管理 ID**（Base32 8 文字）で、**利用者が打つのは表示名**
// （日本語可・一意性なし）。**URL と通信に載るのは識別子だけ**で、表示名は画面に出すために
// 端末が覚える（`telephone.channels`）。したがって**ここでは識別子の中身を読まない**
// — 字種の権威はサーバ（`server/common/src/rooms.rs`）にあり、画面は受け取った文字列を
// そのまま運ぶ（画面仕様 §2 の但し書き）。

/** 招待 URL が指す行き先。 */
export interface Invite {
  /** グループの識別子。ランダムな Base32 10 文字・小文字（ADR-0021 §1-1）。 */
  group: string;
  /**
   * チャンネルの識別子。**表示名ではない**（ADR-0022 §1-1）。
   *
   * 動的チャンネルはサーバ採番の Base32 8 文字、常設の部屋は `rooms.conf` の識別子
   * （いまは `main`）。**どちらも中身は読まない。**
   */
  channel: string;
}

/**
 * 表示名の長さの上限。**サーバ側の `validate_display_name` と同じ値にする**
 * （ADR-0022 §1-2。サーバ側は #162 のパッチで入る）。
 */
export const MAX_DISPLAY_NAME_LEN = 64;

/**
 * グループの識別子として受け取る形。
 *
 * **ADR-0021 の Base32（`i` `l` `o` `u` を除く 32 種・10 文字）より緩くしてある。**
 * 識別子を作るのは運用者（`TELEPHONE_GROUPS`）で、突き合わせはサーバが完全一致で行う
 * （ADR-0021 §1-3・§5-2）。ここで字種まで縛ると、運用者が別の形で書いた途端に
 * 画面だけが拒むことになる。**小文字に揃える決定（§5-2「正規化はしない」）だけを守る。**
 */
const GROUP = /^[a-z0-9]{1,64}$/;

/**
 * チャンネルの識別子として受け取る形。**中身を読まないための最小限の枠**。
 *
 * サーバ採番の Base32 8 文字（ADR-0022 §1-1）も、常設の部屋の識別子（`main`）も通る。
 * **ここで Base32 8 文字に絞らない** — 常設の部屋を弾いてしまうし、採番規則が変わるたびに
 * 画面を直すことになる。URL・ログ・設定に出せる字種（英数字と `-` `_`）だけを見る。
 */
const CHANNEL = /^[A-Za-z0-9_-]{1,64}$/;

/** 表示名に入れてはいけない文字（制御文字。ADR-0022 §1-2）。 */
const CONTROL = /\p{Cc}/u;

/**
 * 招待 URL のフラグメントを読む。
 *
 * `#` は付いていても付いていなくてもよい（`location.hash` はそのまま渡せる）。
 * **読めなければ `null`。** 画面は入力欄（保険）へ落とす。
 *
 * 壊れた URL 符号化（`%E3%81`）は `URLSearchParams` が U+FFFD に替えるので、
 * 字種の検査で落ちて `null` になる（例外にはしない）。
 */
export function parseInvite(hash: string): Invite | null {
  const body = hash.startsWith("#") ? hash.slice(1) : hash;
  if (body === "") return null;

  const room = new URLSearchParams(body).get("room");
  if (!room) return null;

  // 区切りは 1 つだけ。識別子に `/` は入らない（CHANNEL が弾く）ので、
  // 2 つ以上あれば招待 URL ではない
  const parts = room.split("/");
  if (parts.length !== 2) return null;
  const [group, channel] = parts;
  if (group === undefined || channel === undefined) return null;
  if (!GROUP.test(group) || !CHANNEL.test(channel)) return null;
  return { group, channel };
}

/**
 * **グループだけを指す招待 URL** のフラグメントを読む（2026-10-29）。
 *
 * 形は `#group=<コード>`。読めなければ `null`。
 *
 * **チャンネルを指さないので、受け取った人は「どのチャンネルに入るか」を自分で選ぶ。**
 * グループに招くのは通話を始める前の行為なので、行き先を決め打ちしないほうが素直
 * （畳まれたチャンネルを指す招待 URL の弱点＝表示名が出ない、も避けられる）。
 */
export function parseGroupInvite(hash: string): string | null {
  const body = hash.startsWith("#") ? hash.slice(1) : hash;
  if (body === "") return null;

  const group = new URLSearchParams(body).get("group");
  if (!group || !GROUP.test(group)) return null;
  return group;
}

/** 招待 URL が指すもの。**チャンネルまで指すもの**と**グループだけのもの**がある。 */
export type InviteTarget =
  | { kind: "channel"; group: string; channel: string }
  | { kind: "group"; group: string };

/**
 * 招待 URL のフラグメントを読む（**鍵の優先順を 1 か所に置く**。2026-10-29）。
 *
 * **`room=` が読めればそちらを採る**（チャンネルまで指す = より具体的な行き先）。
 * 読めなければ `group=` を見る。どちらも読めなければ `null`。
 *
 * **優先順をここに置くのは、画面の配線（`main.ts`）に判断を持たせないため**
 * （配線は試験できない。DESIGN-0006 §5-1）。
 */
export function parseInviteTarget(hash: string): InviteTarget | null {
  const channel = parseInvite(hash);
  if (channel) return { kind: "channel", group: channel.group, channel: channel.channel };
  const group = parseGroupInvite(hash);
  return group === null ? null : { kind: "group", group };
}

/**
 * **グループの招待 URL を作る**（「グループの招待 URL をコピー」。2026-10-29）。
 *
 * `base` の扱いは {@link buildInviteUrl} と同じ（クエリと元のフラグメントは落とす）。
 * **表示名は載せない** — 載せないことがコードを採った理由の 1 つ（ADR-0021）。
 * 受け取った人の画面には、**サーバが `rooms` で返す表示名**が出る（ADR-0025 §4-1 の 7）。
 */
export function buildGroupInviteUrl(base: string, group: string): string {
  const url = new URL(base);
  url.search = "";
  url.hash = `group=${encodeURIComponent(group)}`;
  return url.toString();
}

/**
 * いま居るチャンネルの招待 URL を作る（「招待 URL をコピー」。DESIGN-0006 §4-1）。
 *
 * `base` には `location.href` をそのまま渡せる。**元のフラグメントとクエリは落とす**
 * （クエリを残すと、フラグメントにした意味が薄れる）。
 *
 * **表示名は載せない**（ADR-0022 §2-2）。受け取った人の画面に名前が出ないことの扱いは
 * DESIGN-0006 §6 の未決。
 */
export function buildInviteUrl(base: string, invite: Invite): string {
  const url = new URL(base);
  url.search = "";
  url.hash = `room=${encodeURIComponent(invite.group)}/${encodeURIComponent(invite.channel)}`;
  return url.toString();
}

/** 入室要求（`join.room`）に入れる識別子にする。 */
export function toRoomId(invite: Invite): string {
  return `${invite.group}/${invite.channel}`;
}

/**
 * `グループ/チャンネル` を 2 つに分ける。**`/` が無ければグループ無し**。
 *
 * {@link parseInvite} と違って**緩い**。一覧の応答（`RoomInfo.id`）には常設の部屋が
 * グループ無しで来る（いまの `rooms.conf` は `main`）ので、ここで弾くと一覧が消える。
 */
export function parseRoomCode(room: string): { group: string; channel: string } {
  const at = room.indexOf("/");
  return at < 0
    ? { group: "", channel: room }
    : { group: room.slice(0, at), channel: room.slice(at + 1) };
}

/** 入室する部屋の識別子。グループを選んでいれば `グループ/チャンネル`。 */
export function composeRoom(group: string, channel: string): string {
  const trimmed = channel.trim();
  if (!trimmed) return "";
  return group ? `${group}/${trimmed}` : trimmed;
}

/**
 * 一覧に出す名前。**覚えている表示名があればそれ**、無ければ識別子（グループの接頭辞を外す）。
 *
 * 表示名を覚えていないのは、招待 URL で来た人と、別の端末で作られたチャンネル
 * （表示名は端末が覚えるだけでサーバに無い。ADR-0022 §1-2）。
 */
export function channelLabel(group: string, id: string, names: Readonly<Record<string, string>> = {}): string {
  const remembered = names[id];
  if (remembered !== undefined && remembered !== "") return remembered;
  return group && id.startsWith(`${group}/`) ? id.slice(group.length + 1) : id;
}

/**
 * **チャンネルの表示名が使えるか。** 使えなければ理由（画面に出す文）を返す。
 *
 * 検査は「空でない・64 文字以内・制御文字なし」だけ（ADR-0022 §1-2。**一意性は求めない**）。
 * **サーバ側でも同じ検査をする**（`validate_display_name`）。ここで見るのは「送る前に
 * 気づける」ためで、信用するのはサーバ側の判断。**規則を変えるときは両方直す。**
 *
 * 長さは**コードポイントで数える**（絵文字 1 つを 2 と数えないため）。**サーバ側が
 * 文字数で数えるか長さ（バイト）で数えるかは #162 のパッチで突き合わせる**
 * （DESIGN-0006 §6 の未決 6）。
 */
export function validateDisplayName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "名前を入れてください";
  if ([...trimmed].length > MAX_DISPLAY_NAME_LEN) {
    return `名前が長すぎます（${MAX_DISPLAY_NAME_LEN} 文字まで）`;
  }
  if (CONTROL.test(trimmed)) return "名前に使えない文字が入っています";
  return null;
}

/**
 * 覚えているグループに 1 つ足したものを返す（**元の配列は変えない**）。
 *
 * 並びは**足した順**。重複と空文字は入れない。
 */
export function addGroup(groups: readonly string[], group: string): string[] {
  if (!group || groups.includes(group)) return [...groups];
  return [...groups, group];
}

/** 保存されていた値を読むときの掃除（壊れていても落ちないように）。 */
export function sanitizeGroups(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const g of raw) {
    if (typeof g === "string" && g !== "" && !out.includes(g)) out.push(g);
  }
  return out;
}

/**
 * 覚えているチャンネルの表示名に 1 つ足したものを返す（**元の対応は変えない**）。
 *
 * 鍵は `グループ/チャンネル` の全体。**畳まれたチャンネルへ入り直すときに
 * `join.room_display_name` へ載せる**（ADR-0022 §1-4）。
 */
export function rememberChannelName(
  names: Readonly<Record<string, string>>,
  room: string,
  displayName: string,
): Record<string, string> {
  const next = { ...names };
  const trimmed = displayName.trim();
  if (room === "" || trimmed === "") return next;
  next[room] = trimmed;
  return next;
}

/** 保存されていた表示名を読むときの掃除（壊れていても落ちないように）。 */
export function sanitizeChannelNames(raw: unknown): Record<string, string> {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [room, name] of Object.entries(raw as Record<string, unknown>)) {
    if (room === "" || typeof name !== "string") continue;
    const trimmed = name.trim();
    if (trimmed === "" || validateDisplayName(trimmed) !== null) continue;
    out[room] = trimmed;
  }
  return out;
}
